import { KeyValue, KeyValuePipe } from "@angular/common";
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationType, ENV, LoggerService } from '@bequeathed/ngx-app-core';
import { AIInterviewSummary, AIService, AnalyticsEventName, InterviewService } from '@bequeathed/ngx-services';
import { Topic } from "@bequeathed/ngx-services/lib/model/ai-interview.model";
import { PageUtil } from '@bequeathed/ngx-utils';
import { ReplaySubject } from "rxjs";
import { BaseForm } from '../../../core/ui/base-form/base-form.component';
import { UiModule } from "../../../core/ui/ui.module";
import { AnalyticsUtils } from "../../../core/utils/analytics.utils";

const LOG: LoggerService = LoggerService.get('SummaryComponent');

@Component({
    selector: 'app-interview-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.sass'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [
        UiModule,
        KeyValuePipe,
    ]
})
export class SummaryComponent extends BaseForm implements OnInit {

    interviewId: number | undefined
    sessionId: string | undefined
    summary: AIInterviewSummary | undefined
    homeAppUri: string | undefined
    DEBUG = ENV.config.get("show-debug") === "true" || ENV.config.get("show-debug") === true

    public updated = new ReplaySubject<boolean>();

    private router = inject(Router);
    private title = inject(Title);
    protected aiService = inject(AIService);
    protected interviewService = inject(InterviewService);
    private route = inject(ActivatedRoute);

    constructor() {
        super()
        this.title.setTitle('Will Interview : Summary')
        this.loading = false

        this.homeAppUri = ENV.applications.get(ApplicationType.HOME)!.baseUrl
    }

    override ngOnInit(): void {

        this.route.params.subscribe((params: any) => {
            this.type = params.type;
            this.interviewId = params.id;
            this.sessionId = params.sessionId;

            this.fetchSummary();
        });

        this.updated.subscribe((updated) => {
            if (updated) {
                LOG.debug('ngOnInit', "Question(s) updated; Fetching new summary");
                this.fetchSummary();
            }
        })
    }

    private fetchSummary(): void {
        this.loading = true;
        this.interviewService.summaryState = this.interviewService.summaryState || {};
        this.interviewService.summaryState.enabled = true;
        this.interviewService.summaryState.editing = false;

        this.analytics.trackInterviewPage(
            `/interview/WILL/${this.interviewId}/${this.sessionId}/summary`,
            {
                customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
                page: `/interview/WILL/${this.interviewId}/${this.sessionId}/summary`,
                interview_id: this.interviewId ?? '',
            }
        )
        this.aiService.summary(this.interviewId!, this.sessionId!)
            .then((res) => {
                this.summary = res;
                const topics = res.topics!
                // TODO: Could this be done via the template instead?
                const firstTopicName = topics.get(
                    topics
                        .keys()
                        .next()
                        .value
                )
                this.toggleTopic(firstTopicName!)
                if (this.interviewService.summaryState?.activeTopics?.length) {
                    this.interviewService.summaryState.editing = false;
                }
                this.loading = false;

                PageUtil.setFocus('interview-summary-header');
            })
    }

    isTopicActive(topicName: string): boolean {
        const index = (this.interviewService.summaryState.activeTopics as [])
            .findIndex((activeTopic: string) => activeTopic === topicName)
        return index > -1;
    }

    public toggleTopic(topic: Topic): void {
        LOG.debug('toggleTopic', `Toggling: ${topic.id}`);

        const activeTopics: string[] = this.interviewService.summaryState.activeTopics;
        if (activeTopics.includes(topic.id!)) {
            this.interviewService.summaryState.activeTopics = activeTopics.filter((id: string) => id !== topic.id);
        } else {
            activeTopics.push(topic.id!)
        }
    }

    async saveAndExit(): Promise<void> {
        LOG.debug('saveAndExit', 'Redirecting to my wishes...');
        this.goToWills();
    }

    goToWills(): void {
        window.location.href = `${this.homeAppUri}/dashboard`;
    }

    async completeInterview(): Promise<void> {
        await this.interviewService.complete(this.type, this.interviewId!);
        this.loading = false;

        // Funnel stage: will_interview_complete. The Will interview is completed
        // from the summary before moving on to the advice stage (design §5).
        this.analytics.trackFunnelStage(AnalyticsEventName.WILL_INTERVIEW_COMPLETE, {
            customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
        });

        await this.router.navigate(['interview', 'ADVICE', this.interviewId, 'recommendations']);
    }

    sortByOrder = (a: KeyValue<string, Topic>, b: KeyValue<string, Topic>) => {
        return a.value!.order! - b.value!.order!;
    }

}
